import { useEffect } from 'react';
import { QueryFunctionContext, useInfiniteQuery, useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import { getEventInfo } from '@state/event';
import { setPeopleId } from '@state/user';

import { list as listFn, getKiosk as getKioskFn, getCurrentUser } from './service';

interface PeopleParams {
  search?: string;
  filter?: string;
  sort?: string;
  sessionId?: string;
}
interface PeopleArguments {
  eventId: string;
  params: PeopleParams;
  enabled?: boolean;
}

type PeopleKey = ['people', string /* eventId */, PeopleParams];

const peopleQuery = {
  getQueryKey: (eventId: string, params: PeopleParams): PeopleKey => ['people', eventId, params],
  queryFn: ({ pageParam, queryKey: [, eventId, params] }: QueryFunctionContext<PeopleKey>) =>
    listFn(eventId, {
      ...params,
      page: pageParam || 0,
    }),
};

export const usePeople = ({
  eventId,
  params: { search, filter, sort, sessionId },
  enabled = true,
}: PeopleArguments) =>
  useInfiniteQuery({
    queryKey: peopleQuery.getQueryKey(eventId, { search, filter, sort, sessionId }),
    queryFn: peopleQuery.queryFn,
    enabled,
    cacheTime: 0,
  });

interface KioskPersonArguments {
  eventId: string;
  search: string;
}

type KioskPersonKey = ['kioskPerson', string /* eventId */, string /* search */];

const kioskPersonQuery = {
  getQueryKey: (eventId: string, search: string): KioskPersonKey => [
    'kioskPerson',
    eventId,
    search,
  ],
  queryFn: ({ pageParam, queryKey: [, eventId, search] }: QueryFunctionContext<KioskPersonKey>) =>
    getKioskFn(eventId, search, pageParam),
};

export const useKioskPerson = ({
  eventId,
  search,
  enabled,
}: KioskPersonArguments & { enabled: boolean }) =>
  useInfiniteQuery({
    queryKey: kioskPersonQuery.getQueryKey(eventId, search),
    queryFn: kioskPersonQuery.queryFn,
    enabled,
    cacheTime: 0,
  });

type CurrentUserKey = ['me', string? /* eventId */];
const currentUserQuery = {
  getQueryKey: (eventId?: string): CurrentUserKey => ['me', eventId],
  queryFn: ({ queryKey: [, eventId] }: QueryFunctionContext<CurrentUserKey>) =>
    getCurrentUser(eventId ?? ''),
};

export const useSetPeopleId = () => {
  const dispatch = useDispatch();
  const eventInfo = useSelector(getEventInfo);

  const { isLoading, data, isFetching } = useQuery({
    queryKey: currentUserQuery.getQueryKey(eventInfo.id),
    queryFn: currentUserQuery.queryFn,
    enabled: !!eventInfo?.id,
  });

  useEffect(() => {
    if (data?.id) {
      dispatch(setPeopleId(data.id));
    }
  }, [data, dispatch]);

  return { isLoading: isLoading || isFetching };
};
